import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { XMarkIcon, PhoneIcon, EnvelopeIcon, CalendarIcon, DocumentTextIcon, PresentationChartBarIcon, ComputerDesktopIcon, EllipsisHorizontalCircleIcon } from '@heroicons/react/24/outline';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';
import { prospectTaskSchema } from '../../lib/validation';
import type { ProspectTaskData } from '../../lib/validation';

interface Task {
  id: string;
  task_type: string;
  title: string;
  due_date?: string;
  priority: string;
  notes?: string;
}

interface ProspectTaskSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  prospectId: string;
  companyName: string;
  task?: Task | null;
}

const TASK_TYPES = [
  { value: 'call', label: 'Call', icon: PhoneIcon },
  { value: 'email', label: 'Email', icon: EnvelopeIcon },
  { value: 'meeting', label: 'Meeting', icon: CalendarIcon },
  { value: 'follow_up', label: 'Follow-up', icon: DocumentTextIcon },
  { value: 'proposal', label: 'Proposal', icon: PresentationChartBarIcon },
  { value: 'demo', label: 'Demo', icon: ComputerDesktopIcon },
  { value: 'other', label: 'Other', icon: EllipsisHorizontalCircleIcon },
] as const;

const PRIORITY_CONFIG = {
  low:    { label: 'Low',    color: 'border-gray-300 text-gray-600 bg-gray-50' },
  medium: { label: 'Medium', color: 'border-blue-300 text-blue-700 bg-blue-50' },
  high:   { label: 'High',   color: 'border-orange-300 text-orange-700 bg-orange-50' },
  urgent: { label: 'Urgent', color: 'border-red-300 text-red-700 bg-red-50' },
};

export default function ProspectTaskSheet({ isOpen, onClose, onSuccess, prospectId, companyName, task }: ProspectTaskSheetProps) {
  const isEditing = !!task;
  const { register, handleSubmit, reset, watch, setValue, formState: { errors, isSubmitting } } = useForm<ProspectTaskData>({
    resolver: zodResolver(prospectTaskSchema),
    defaultValues: { task_type: 'follow_up', priority: 'medium' },
  });

  const taskType = watch('task_type');
  const priority = watch('priority');

  useEffect(() => {
    if (task) {
      reset({
        task_type: task.task_type as ProspectTaskData['task_type'],
        title: task.title,
        due_date: task.due_date || '',
        priority: task.priority as ProspectTaskData['priority'],
        notes: task.notes || '',
      });
    } else {
      reset({ task_type: 'follow_up', priority: 'medium', title: '' });
    }
  }, [task, reset, isOpen]);

  useEffect(() => {
    if (!isEditing && taskType) {
      const typeLabel = TASK_TYPES.find(t => t.value === taskType)?.label || '';
      setValue('title', `${typeLabel} with ${companyName}`);
    }
  }, [taskType, companyName, isEditing, setValue]);

  const onSubmit = async (data: ProspectTaskData) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (isEditing && task) {
        const { error } = await supabase.from('prospect_tasks').update({
          task_type: data.task_type,
          title: data.title,
          due_date: data.due_date || null,
          priority: data.priority,
          notes: data.notes || null,
        }).eq('id', task.id);
        if (error) throw error;
        toast.success('Task updated');
      } else {
        const { data: newTask, error } = await supabase.from('prospect_tasks').insert([{
          prospect_id: prospectId,
          task_type: data.task_type,
          title: data.title,
          due_date: data.due_date || null,
          priority: data.priority,
          notes: data.notes || null,
          created_by: user?.id,
        }]).select().single();
        if (error) throw error;

        await supabase.from('prospect_activities').insert([{
          prospect_id: prospectId,
          activity_type: 'task_created',
          title: `Task created: ${data.title}`,
          metadata: { task_id: newTask.id, task_type: data.task_type, due_date: data.due_date },
          performed_by: user?.id,
        }]);
        toast.success('Task added');
      }

      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save task');
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-end sm:items-center justify-center p-0 sm:p-4">
            <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 translate-y-4 sm:scale-95" enterTo="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-150" leaveFrom="opacity-100 translate-y-0 sm:scale-100" leaveTo="opacity-0 translate-y-4 sm:scale-95">
              <Dialog.Panel className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-xl">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                  <Dialog.Title className="text-base font-semibold text-golf-gray">
                    {isEditing ? 'Edit Task' : 'Add Task'}
                  </Dialog.Title>
                  <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
                    <XMarkIcon className="w-5 h-5 text-gray-500" />
                  </button>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className="p-5 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Task Type</label>
                    <div className="grid grid-cols-4 gap-2">
                      {TASK_TYPES.map(({ value, label, icon: Icon }) => (
                        <button key={value} type="button" onClick={() => setValue('task_type', value)}
                          className={`flex flex-col items-center gap-1 p-2 rounded-lg border text-xs font-medium transition-colors ${taskType === value ? 'border-golf-orange bg-golf-orange/10 text-golf-orange' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                          <Icon className="w-4 h-4" />
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                    <input {...register('title')} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-golf-orange/40 focus:border-golf-orange" />
                    {errors.title && <p className="mt-1 text-xs text-red-600">{errors.title.message}</p>}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                      <input type="date" {...register('due_date')} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-golf-orange/40 focus:border-golf-orange" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                      <div className="flex gap-1 flex-wrap">
                        {(Object.entries(PRIORITY_CONFIG) as [ProspectTaskData['priority'], typeof PRIORITY_CONFIG[keyof typeof PRIORITY_CONFIG]][]).map(([v, cfg]) => (
                          <button key={v} type="button" onClick={() => setValue('priority', v)}
                            className={`px-2 py-1 text-xs rounded-md border font-medium transition-colors ${priority === v ? cfg.color : 'border-gray-200 text-gray-500 bg-white hover:bg-gray-50'}`}>
                            {cfg.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <textarea {...register('notes')} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-golf-orange/40 focus:border-golf-orange resize-none" />
                  </div>

                  <div className="flex justify-end gap-3 pt-1">
                    <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">Cancel</button>
                    <button type="submit" disabled={isSubmitting} className="px-4 py-2 text-sm font-medium text-white bg-golf-orange rounded-lg hover:bg-golf-orange/90 transition-colors disabled:opacity-60">
                      {isSubmitting ? 'Saving...' : isEditing ? 'Save Task' : 'Add Task'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
